/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState } from "react";
import Highlighter from "react-highlight-words";
import moment from "moment";
import { Link } from "react-router-dom";
import { RichEditorPopup } from "../formFields/RichEditorPopup";
import Portal from "../Portal";
import { formatSpecialPermissionLabels } from "../../../users/constants";
import { addToBreadCrumpData } from "../../../../config/utils";
import { getExecutiveSummaryChangesetFromLogItem } from "../../../building/components/executiveSummaryLogCompare";

const FIELD_LABEL_OVERRIDES = {
    executive_summary: "Executive Summary",
    special_permissions: "Special Permissions",
    qa_qc_status: "QA Status",
    qa_passed: "QA/QC Passed",
    qa_ready: "Ready for QA",
    qa_ready_at: "Ready for QA On",
    qa_passed_at: "QA/QC Passed On",
    review_status: "Review Status"
};

const QA_STATUS_VALUE_LABELS = {
    in_progress: "In Progress",
    ready_for_qa: "Ready for QA",
    qa_passed: "QA Passed",
    reopened: "Reopened"
};

const BOOLEAN_VALUE_FIELDS = ["qa_passed", "qa_ready"];

const QA_DERIVED_FIELDS = ["qa_passed", "qa_ready", "qa_ready_at", "qa_passed_at"];

const CREATION_METHOD_LABELS = {
    similar: "Similar",
    duplicate_full: "Duplicate - Full",
    duplicate_no_csp: "Duplicate - No CSP",
    same_geo_hierarchy: "Same Geo Hierarchy"
};

const SOURCE_RECOMMENDATION_FIELDS = ["source_recommendation_id", "source_recommendation_code", "creation_method"];

const CR_LINK_FIELDS = ["linked_recommendation", "linked_capital_request"];

const isDynamicFieldRulesChange = nestedField => nestedField.includes(".rules.");

export const HistoryRow = ({
    item,
    historyParams,
    integerValues,
    handleRestoreLog,
    hasLogRestore,
    handleDeleteLog,
    hasLogDelete,
    columnConfig,
    onViewExecutiveSummaryChanges
}) => {
    const [richTextPopup, setRichTextPopup] = useState({
        label: "",
        key: "",
        value: "",
        show: false
    });

    const execSummaryChangeset = getExecutiveSummaryChangesetFromLogItem(item);
    const canViewExecutiveSummaryChanges = !!execSummaryChangeset && typeof onViewExecutiveSummaryChanges === "function";
    const showActionColumn = hasLogDelete || hasLogRestore || canViewExecutiveSummaryChanges;

    const fromValueStyle = {
        backgroundColor: "#ffe6e6",
        padding: "4px",
        borderRadius: "3px",
        display: "inline-block",
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        marginBottom: "5px"
    };

    const toValueStyle = {
        backgroundColor: "#e6ffe6",
        padding: "4px",
        borderRadius: "3px",
        display: "inline-block",
        whiteSpace: "pre-wrap",
        wordBreak: "break-word"
    };

    const sourceLinkStyle = {
        color: "#007bff",
        textDecoration: "underline",
        fontWeight: 600
    };

    const isRichText = value => typeof value === "string" && /<[^>]+>/.test(value);

    const formatValueWithHighlight = (value, isFromValue) => <div style={isFromValue ? fromValueStyle : toValueStyle}>{value || "null"}</div>;

    const isDateField = field => {
        const cleanField = field?.endsWith(".value") ? field.replace(".value", "") : field;
        const fieldConfig = columnConfig?.[cleanField];
        return fieldConfig?.type === "date" || ["inspection_date", "deleted_at"].includes(cleanField) || cleanField?.endsWith("_date");
    };

    const formatDateValue = value => {
        if (!value) return "null";
        const parsedDate = moment(value, [moment.ISO_8601, "YYYY-MM-DD", "MM-DD-YYYY"], true);
        return parsedDate.isValid() ? parsedDate.format("MM-DD-YYYY") : value;
    };

    const formatRichText = (value, label) => {
        // Determine whether to truncate the value
        const plainText = value ? value.replace(/<[^>]+>/g, "") : "null";
        const shouldTruncate = plainText.length > 100;
        const displayText = shouldTruncate ? `${plainText.slice(0, 100)}...` : plainText;

        return (
            <div style={{ display: "block", position: "relative", padding: "10px", border: "1px solid #ccc", borderRadius: "5px" }}>
                <span className="pr-5">{displayText}</span>
                <button
                    type="button"
                    className="butn-expansion"
                    style={{
                        position: "absolute",
                        top: "5px",
                        right: "5px",
                        backgroundColor: "transparent",
                        border: "none",
                        cursor: "pointer"
                    }}
                    onClick={() =>
                        setRichTextPopup({
                            label,
                            value,
                            show: true,
                            key: label
                        })
                    }
                >
                    <img src="/img/expand1.svg" alt="Expand" style={{ width: "20px", height: "20px" }} />
                </button>
            </div>
        );
    };

    const formatSpecialPermissionsValue = value => {
        if (value == null) return "null";
        const slugs = Array.isArray(value) ? value : [];
        const formatted = formatSpecialPermissionLabels({ special_permissions: slugs });
        return formatted || "null";
    };

    const formatValue = (field, value, fieldLabel = "") => {
        if (field === "special_permissions") {
            return formatSpecialPermissionsValue(value);
        }

        if (field === "qa_qc_status" && QA_STATUS_VALUE_LABELS[value]) {
            return QA_STATUS_VALUE_LABELS[value];
        }

        if (BOOLEAN_VALUE_FIELDS.includes(field)) {
            if (value === true || value === "true") return "Yes";
            if (value === false || value === "false") return "No";
        }

        if (isRichText(value)) {
            return formatRichText(value, fieldLabel);
        }

        if (integerValues.includes(field) || (typeof value === "number" && field !== "installed_year" && field !== "birth_year")) {
            return value?.toLocaleString() || "null";
        } else if (isDateField(field)) {
            return formatDateValue(value);
        } else if (typeof value === "string") {
            if (!isNaN(value)) return value;

            try {
                const parsed = JSON.parse(value);
                if (Array.isArray(parsed) && parsed.length === 0) return "null";
                if (typeof parsed === "object" && Object.keys(parsed).length === 0) return "null";

                // General case for other JSON-like data (objects)
                if (typeof parsed === "object") {
                    return (
                        <ul>
                            {Object.entries(parsed).map(([key, val], index) => (
                                <li key={index}>
                                    <b>{key}:</b> {typeof val === "object" ? JSON.stringify(val, null, 2) : val || "N/A"}
                                </li>
                            ))}
                        </ul>
                    );
                }

                // If it's an array, show all items
                if (Array.isArray(parsed)) {
                    return (
                        <ul>
                            {parsed.map((item, index) => (
                                <li key={index}>
                                    {typeof item === "object"
                                        ? Object.entries(item).map(([subKey, subValue]) => (
                                              <div key={subKey}>
                                                  <b>{subKey}:</b> {subValue || "N/A"}
                                              </div>
                                          ))
                                        : item || "N/A"}
                                </li>
                            ))}
                        </ul>
                    );
                }

                return <pre>{JSON.stringify(parsed, null, 2)}</pre>;
            } catch (e) {
                return value || "null";
            }
        } else if (typeof value === "object") {
            // Handle objects directly
            if (value?.value) {
                if (isRichText(value?.value)) {
                    return formatRichText(value?.value, fieldLabel);
                }
                if (isDateField(field)) {
                    return formatDateValue(value.value);
                }
                return value?.value;
            } else {
                return "null"; // Handle empty objects
            }
        }
        return value?.toString() || "null";
    };

    const formatFieldLabel = field => {
        const cleanField = field.endsWith(".value") ? field.replace(".value", "") : field;
        if (FIELD_LABEL_OVERRIDES[cleanField]) {
            return FIELD_LABEL_OVERRIDES[cleanField];
        }
        if (!columnConfig || typeof columnConfig !== "object") {
            return cleanField.replace("_id", "");
        }
        const fieldKey = Object.keys(columnConfig).find(key => cleanField === key) || "";
        if (fieldKey && columnConfig[fieldKey]?.label) {
            return columnConfig[fieldKey].label;
        }
        return cleanField.replace("_id", "");
    };

    const handleSourceRecommendationClick = (path, code) => {
        addToBreadCrumpData({ key: "Name", name: code, path });
        addToBreadCrumpData({ key: "info", name: "Main Details", path });
    };

    const renderSourceRecommendationRow = changeset => {
        const method = changeset?.creation_method?.[1];
        const code = changeset?.source_recommendation_code?.[1];
        const sourceId = changeset?.source_recommendation_id?.[1];
        if (!method && !code) return null;

        const methodLabel = CREATION_METHOD_LABELS[method] || method;
        const createdCode = changeset?.recommendation?.[1];
        const searchWords = [historyParams?.search || ""];
        const sourcePath = `/recommendations/recommendationsinfo/${sourceId}/maindetails`;

        return (
            <li key="source-recommendation">
                <b>
                    <Highlighter searchWords={searchWords} textToHighlight={item.user || "System"} />
                </b>{" "}
                {createdCode ? (
                    <>
                        created recommendation{" "}
                        <b>
                            <Highlighter searchWords={searchWords} textToHighlight={createdCode} />
                        </b>
                    </>
                ) : (
                    "created this recommendation"
                )}
                {methodLabel ? (
                    <>
                        {" "}
                        as{" "}
                        <b>
                            <Highlighter searchWords={searchWords} textToHighlight={methodLabel} />
                        </b>
                    </>
                ) : null}
                {code ? (
                    <>
                        {" "}
                        from{" "}
                        {sourceId ? (
                            <Link
                                style={sourceLinkStyle}
                                to={{ pathname: sourcePath, state: { fromSourceRecommendation: true } }}
                                onClick={() => handleSourceRecommendationClick(sourcePath, code)}
                            >
                                <Highlighter searchWords={searchWords} textToHighlight={code} />
                            </Link>
                        ) : (
                            <b>
                                <Highlighter searchWords={searchWords} textToHighlight={code} />
                            </b>
                        )}
                    </>
                ) : null}
            </li>
        );
    };

    const renderCrLinkRow = (field, values, key) => {
        const [fromCode, toCode] = Array.isArray(values) ? values : [null, null];
        const linked = !!toCode;
        const code = String(toCode || fromCode || "");
        if (!code) return null;

        const searchWords = [historyParams?.search || ""];
        const isRecommendationCode = field === "linked_recommendation";

        return (
            <li key={key}>
                <b>
                    <Highlighter searchWords={searchWords} textToHighlight={item.user || "System"} />
                </b>{" "}
                {linked ? "linked" : "unlinked"}{" "}
                {isRecommendationCode ? (
                    <>
                        recommendation{" "}
                        <b>
                            <Highlighter searchWords={searchWords} textToHighlight={code} />
                        </b>{" "}
                        {linked ? "to" : "from"} this Capital Request
                    </>
                ) : (
                    <>
                        this recommendation {linked ? "to" : "from"} Capital Request{" "}
                        <b>
                            <Highlighter searchWords={searchWords} textToHighlight={code} />
                        </b>
                    </>
                )}
            </li>
        );
    };

    const renderChangeset = changeset => {
        const hasQaStatusChange = changeset.hasOwnProperty("qa_qc_status");
        const hasSourceRecommendation = !!(changeset?.creation_method?.[1] || changeset?.source_recommendation_code?.[1]);
        const filteredEntries = Object.entries(changeset).filter(([field]) => {
            if (hasQaStatusChange && QA_DERIVED_FIELDS.includes(field)) {
                return false;
            }
            const nameMatch = field.match(/(.+)_name$/);
            if (nameMatch) {
                const base = nameMatch[1];
                return !changeset.hasOwnProperty(`${base}_id`);
            }
            return true;
        });

        const rows = filteredEntries.flatMap(([field, values], index) => {
            if (field === "dynamic_fields" && Array.isArray(values)) {
                if (values?.length) {
                    return values.flatMap((dynamicField, dynamicIndex) =>
                        Object.entries(dynamicField)
                            .filter(([nestedField]) => !isDynamicFieldRulesChange(nestedField))
                            .map(([nestedField, nestedValues]) => {
                                const fieldLabel = formatFieldLabel(nestedField);
                                const fromValue = formatValueWithHighlight(formatValue(nestedField, nestedValues[0], fieldLabel), true);
                                const toValue = formatValueWithHighlight(formatValue(nestedField, nestedValues[1], fieldLabel), false);
                                const shouldRenderMultiLine = isRichText(nestedValues[0]) || isRichText(nestedValues[1]);

                                return (
                                    <li key={`${index}-${dynamicIndex}-${nestedField}`}>
                                        <b>
                                            <Highlighter searchWords={[historyParams?.search || ""]} textToHighlight={item.user || "System"} />
                                        </b>{" "}
                                        changed the Dynamic Field{" "}
                                        <b>
                                            <Highlighter searchWords={[historyParams?.search || ""]} textToHighlight={fieldLabel} />
                                        </b>{" "}
                                        {renderFormattedValues(fromValue, toValue, shouldRenderMultiLine)}
                                    </li>
                                );
                            })
                    );
                } else {
                    return (
                        <li key={`${index}`}>
                            <b>
                                <Highlighter searchWords={[historyParams?.search || ""]} textToHighlight={"System"} />
                            </b>{" "}
                            update to the{" "}
                            <b>
                                <Highlighter searchWords={[historyParams?.search || ""]} textToHighlight={"Dynamic Field "} />
                            </b>{" "}
                            detected due to recent modifications in its settings.
                        </li>
                    );
                }
            }
            if (field === "maintenance_year_data_for_sorting" || field === "maintenance_years_data") {
                return (
                    <li key={`${index}`}>
                        <b>
                            <Highlighter searchWords={[historyParams?.search || ""]} textToHighlight={"System"} />
                        </b>{" "}
                        updated the{" "}
                        <b>
                            <Highlighter searchWords={[historyParams?.search || ""]} textToHighlight={field} />
                        </b>{" "}
                        table due to a modification to the maintenance year by <b>{item.user || "System"}</b>.
                    </li>
                );
            }
            if (
                field === "capital_type" ||
                field === "recommendation_capital_type_id" ||
                field === "surveyor_id" ||
                field === "note_xml" ||
                SOURCE_RECOMMENDATION_FIELDS.includes(field) ||
                (hasSourceRecommendation && field === "recommendation")
            ) {
                return null;
            }

            if (CR_LINK_FIELDS.includes(field)) {
                return renderCrLinkRow(field, values, index);
            }

            if (field === "executive_summary") {
                const fieldLabel = formatFieldLabel(field);
                return (
                    <li key={index}>
                        <b>
                            <Highlighter searchWords={[historyParams?.search || ""]} textToHighlight={item.user || "System"} />
                        </b>{" "}
                        {item.event === "restore" ? "restored the " : "changed the "}
                        <b>
                            <Highlighter searchWords={[historyParams?.search || ""]} textToHighlight={fieldLabel} />
                        </b>
                    </li>
                );
            }
            if (item.event === "created") {
                const fieldLabel = formatFieldLabel(field);
                const toValue = formatValueWithHighlight(formatValue(field, values[1], fieldLabel), false);

                return (
                    <li key={index}>
                        <b>
                            <Highlighter searchWords={[historyParams?.search || ""]} textToHighlight={item.user || "System"} />
                        </b>{" "}
                        created{" "}
                        <b>
                            <Highlighter searchWords={[historyParams?.search || ""]} textToHighlight={fieldLabel} />
                        </b>{" "}
                        {toValue}
                    </li>
                );
            }

            const fieldLabel = formatFieldLabel(field);
            const fromValue = formatValueWithHighlight(formatValue(field, values[0], fieldLabel), true);
            const toValue = formatValueWithHighlight(formatValue(field, values[1], fieldLabel), false);
            const shouldRenderMultiLine = isRichText(values[0]) || isRichText(values[1]);
            return (
                <li key={index}>
                    <b>
                        <Highlighter searchWords={[historyParams?.search || ""]} textToHighlight={item.user || "System"} />
                    </b>{" "}
                    {item.event === "restore" ? "restored" : "changed"} the field{" "}
                    <b>
                        <Highlighter searchWords={[historyParams?.search || ""]} textToHighlight={fieldLabel} />
                    </b>{" "}
                    {renderFormattedValues(fromValue, toValue, shouldRenderMultiLine)}
                </li>
            );
        });

        const sourceRow = renderSourceRecommendationRow(changeset);
        return sourceRow ? [sourceRow, ...rows] : rows;
    };

    const renderFormattedValues = (fromValue, toValue, isMultiLine = false) => {
        if (!isMultiLine) {
            return (
                <span>
                    from {fromValue || "null"} to {toValue || "null"}
                </span>
            );
        } else {
            return (
                <>
                    from{" "}
                    <div>
                        {fromValue || "null"}
                        <br />
                        <span>to</span>
                        <br />
                        {toValue || "null"}
                    </div>
                </>
            );
        }
    };
    return (
        <>
            <tr>
                <td className="img-sq-box">
                    <img src="/img/sq-box.png" alt="Notification Icon" />
                </td>
                <td>
                    <ul>
                        {item.associated_changes?.length
                            ? item.associated_changes.map((change, i) => renderChangeset(change.changeset))
                            : renderChangeset(item.changeset)}
                    </ul>
                </td>
                <td>
                    <div className="date">
                        <span>{item.created_at}</span>
                    </div>
                </td>
                {showActionColumn && (
                    <td>
                        <li className="dropdown dot-icn-arw">
                            {item.restore &&
                                hasLogRestore &&
                                item.changeset &&
                                !item.changeset.hasOwnProperty("maintenance_year_data_for_sorting") &&
                                !(
                                    Object.keys(item.changeset).length === 1 &&
                                    item.changeset.dynamic_fields &&
                                    (item.changeset.dynamic_fields?.length ?? 0) === 0
                                ) && (
                                    <a
                                        className="ref"
                                        onClick={() => handleRestoreLog(item.id, "restore", item.changeset, item.associated_changes)}
                                        data-tip="Restore"
                                        data-effect="solid"
                                        data-place="top"
                                        data-background-color="#007bff"
                                    >
                                        <i className="fas fa-history cursor-hand" />
                                    </a>
                                )}

                            {canViewExecutiveSummaryChanges ? (
                                <a
                                    className="ref"
                                    onClick={() =>
                                        onViewExecutiveSummaryChanges(execSummaryChangeset, {
                                            user: item.user,
                                            createdAt: item.created_at
                                        })
                                    }
                                    data-tip="View changes"
                                    data-effect="solid"
                                    data-place="top"
                                    data-background-color="#007bff"
                                >
                                    <i className="fas fa-exchange-alt cursor-hand" />
                                </a>
                            ) : null}

                            {hasLogDelete && (
                                <a
                                    className="del"
                                    onClick={() => handleDeleteLog(item.id, "delete")}
                                    data-tip="Delete"
                                    data-effect="solid"
                                    data-place="top"
                                    data-background-color="#007bff"
                                >
                                    <i className="fas fa-trash cursor-hand" />
                                </a>
                            )}
                        </li>
                    </td>
                )}
            </tr>

            {richTextPopup.show && (
                <Portal
                    body={
                        <RichEditorPopup
                            value={richTextPopup.value}
                            label={richTextPopup.label}
                            onCancel={() => setRichTextPopup({ show: false })}
                            isReadOnly
                        />
                    }
                    onCancel={() => setRichTextPopup({ show: false })}
                />
            )}

        </>
    );
};
